import SelectInput from "../common/SelectInput";
import { useTranslation } from "react-i18next";
import {
  DeploymentResponseDto,
  LocationResponseDto,
  ProjectResponseDto,
  SensorResponseDto,
  MediaQueryCounts,
} from "../common/ObjectTypes";
import {
  InferenceStateFilter,
  ObservationTypeFilter,
  ReviewStatusFilter,
} from "../common/QueryTypes";
import tailwindClassnames from "../common/TailwindClassnames";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { getDeployments } from "../../api/deploymentsApi";
import { getLocations } from "../../api/locationsApi";
import { getSensors } from "../../api/sensorsApi";
import { useNavigate, useParams } from "react-router-dom";
import ReviewDateRangeInput, {
  DateRangeDraft,
  dateRangeErrorForApply,
} from "./ReviewDateRangeInput";
import ConfidenceRangeInput from "./ConfidenceRangeInput";
import TaxonMultiSelect from "./TaxonMultiSelect";
import {
  emptyFilterState,
  ReviewFilterState,
  validateFilters,
} from "./mediaQueryBuilder";
import { isTopLayerOpen } from "../common/topLayer";

/**
 * Imperative handle exposed by ReviewPageFilter for the parent's chip bar to
 * clear the deployment scope filter and re-apply the query without reopening
 * the filter panel. Location and Sensor are plain `ReviewFilterState` fields
 * now (like Species), so the parent clears those directly instead of going
 * through this ref — only Deployment still drives the URL/scope and needs the
 * filter panel's own state (`selectedDeployments`) reset in lockstep.
 */
export type ReviewPageFilterHandle = {
  clearScopeFilter: (dimension: "deployment") => Promise<void>;
};

/** Elements the modal's Tab-trap and initial-focus logic consider focusable. */
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// LocalStorage key constants for review page filter caching
const STORAGE_KEYS = {
  PROJECT_ID: "cachedProjectId",
  LOCATION_ID: "cachedLocationId",
  SENSOR_ID: "cachedSensorId",
  DEPLOYMENT_ID: "cachedDeploymentId",
} as const;

/**
 * Helper function to clear all cached filter values from localStorage
 */
const clearCachedFilters = () => {
  localStorage.removeItem(STORAGE_KEYS.PROJECT_ID);
  localStorage.removeItem(STORAGE_KEYS.LOCATION_ID);
  localStorage.removeItem(STORAGE_KEYS.SENSOR_ID);
  localStorage.removeItem(STORAGE_KEYS.DEPLOYMENT_ID);
};

/**
 * Helper function to get a cached filter value from localStorage
 * @param key The storage key to retrieve
 * @returns The parsed value or null if not found or invalid
 */
const getCachedFilterValue = (key: string): number | null => {
  const value = localStorage.getItem(key);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Helper function to set a cached filter value in localStorage
 * @param key The storage key to set
 * @param value The value to store (will be JSON stringified)
 */
const setCachedFilterValue = (key: string, value: number) => {
  localStorage.setItem(key, JSON.stringify(value));
};

/**
 * Helper function to get a cached array of filter ids from localStorage.
 * Location/Sensor/Deployment moved from a single cached id to a set of ids;
 * a value cached before that change parses to a non-array and is treated as
 * "no cached filter" here rather than migrated.
 * @param key The storage key to retrieve
 * @returns The parsed array, or null if not found or not an array
 */
const getCachedFilterArray = (key: string): number[] | null => {
  const value = localStorage.getItem(key);
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Helper function to cache a set of filter ids in localStorage, or clear the
 * key entirely when the set is empty.
 * @param key The storage key to set
 * @param values The ids to store (will be JSON stringified), or an empty array to clear
 */
const setCachedFilterArray = (key: string, values: number[]) => {
  if (values.length === 0) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, JSON.stringify(values));
  }
};

type ReviewPageFilterProps = {
  /** Whether the modal is currently shown. The component stays mounted while
   * hidden (so its fetched option lists survive being closed/reopened), so
   * this drives the focus/keyboard-trap effects rather than mount/unmount. */
  isOpen: boolean;
  setLoadingPage: (loading: boolean) => void;
  loadingProjects: boolean;
  projects: ProjectResponseDto[];
  selectedProject: ProjectResponseDto | null;
  setSelectedProject: (project: ProjectResponseDto | null) => void;
  /** The page's *applied* deployment scope. Seeds the modal's draft each time
   * it opens; only Apply and Reset write it back (via `setSelectedDeployments`). */
  selectedDeployments: DeploymentResponseDto[];
  setSelectedDeployments: (deployments: DeploymentResponseDto[]) => void;
  applyFilters: (
    project: ProjectResponseDto | null,
    deployments: DeploymentResponseDto[],
    isWholeProject: boolean,
  ) => Promise<void>;
  onClose: () => void;
  /** The page's *applied* filter state — what the grid and the chip row show.
   * Seeds the modal's draft each time it opens; field edits never touch it,
   * only Apply and Reset write it back (via `updateFilterState`). */
  filterState: ReviewFilterState;
  updateFilterState: (partial: Partial<ReviewFilterState>) => void;
  counts: MediaQueryCounts | null;
};

const ReviewPageFilter = forwardRef<
  ReviewPageFilterHandle,
  ReviewPageFilterProps
>(function ReviewPageFilter(
  {
    isOpen,
    setLoadingPage,
    loadingProjects,
    projects,
    selectedProject,
    setSelectedProject,
    selectedDeployments,
    setSelectedDeployments,
    applyFilters,
    onClose,
    filterState,
    updateFilterState,
    counts,
  },
  ref,
) {
  const { t } = useTranslation();
  const [loadingDeployments, setLoadingDeployments] = useState<boolean>(false);
  const [loadingLocations, setLoadingLocations] = useState<boolean>(false);
  const [loadingSensors, setLoadingSensors] = useState<boolean>(false);
  const [deployments, setDeployments] = useState<DeploymentResponseDto[]>([]);
  const [sensors, setSensors] = useState<SensorResponseDto[]>([]);
  const [locations, setLocations] = useState<LocationResponseDto[]>([]);
  const navigate = useNavigate();
  const { projectGuid, deploymentGuid, imageName } = useParams<{
    imageName: string;
    deploymentGuid: string;
    projectGuid: string;
  }>();
  // Track if we've already restored cached filters
  const hasRestoredFilters = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  // The filter fields, below the header row. Initial focus is sought here so it
  // lands on the first field rather than the Close button (AKL-764 AC4).
  const bodyRef = useRef<HTMLDivElement>(null);

  /**
   * The modal's draft. Every field edits this, not the page's `filterState`/
   * `selectedDeployments`, so the page's chip row and "Filters" badge only
   * ever show what has actually been applied (QA Swarm UX-001/UX-002 on
   * AKL-764: a staged Reset emptied the chips while the grid stayed filtered).
   * Apply and Reset commit the draft; Cancel/Close/Escape/overlay just close,
   * and the seed effect below throws the draft away on the next open.
   */
  const [draft, setDraftState] = useState<ReviewFilterState>(filterState);
  const [draftDeployments, setDraftDeploymentsState] =
    useState<DeploymentResponseDto[]>(selectedDeployments);
  // Refs mirror the draft synchronously so `handleApply` can read the value
  // set in the *same* key event: react-datepicker's Enter on a highlighted
  // day fires our onChange, and the document listener below then applies —
  // before React has re-rendered the state. Reading the state there would
  // apply the previous date and close the modal without the chosen day
  // (Copilot, PR #792).
  const draftRef = useRef<ReviewFilterState>(filterState);
  const draftDeploymentsRef =
    useRef<DeploymentResponseDto[]>(selectedDeployments);
  const setDraft = useCallback((next: ReviewFilterState) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);
  const setDraftDeployments = useCallback((next: DeploymentResponseDto[]) => {
    draftDeploymentsRef.current = next;
    setDraftDeploymentsState(next);
  }, []);
  // False until the reviewer edits a field after the last seed. While the
  // draft is pristine it keeps following the applied state (effect below), so
  // a cached-filter restore or deep-link resolution that lands *after* the
  // modal was opened still shows up in the fields — and Apply then commits
  // the restored scope instead of the empty draft the open had captured
  // (Copilot, PR #792).
  const draftDirtyRef = useRef(false);
  const updateDraft = (partial: Partial<ReviewFilterState>) => {
    draftDirtyRef.current = true;
    setDraft({ ...draftRef.current, ...partial });
  };
  const editDraftDeployments = (next: DeploymentResponseDto[]) => {
    draftDirtyRef.current = true;
    setDraftDeployments(next);
  };
  // Bumped on every open; keys the date-range control so it remounts. Its
  // pickers keep the text being typed in local state and only re-sync it when
  // the selected date changes, so a half-typed date that never became a date
  // would otherwise survive Cancel and still be there on the next open. The
  // confidence inputs need no such key: they re-sync on blur, and the
  // initial-focus effect below blurs them by moving focus to the first field
  // on every open (covered by a test).
  const [openSeq, setOpenSeq] = useState(0);

  // What the date boxes show (AKL-1007): a typed date the picker refused stays
  // on screen, and applying the draft without it would filter on dates the
  // reviewer cannot see. A ref, not state: `handleApply` reads it in the same
  // key event that may have just accepted a date (see
  // `dateRangeErrorForApply`). The "format" message only shows after Apply
  // has been refused, so a half-typed date does not flash an error; it hides
  // again once the boxes can be applied.
  const dateRangeDraftRef = useRef<DateRangeDraft | null>(null);
  const [showDateFormatError, setShowDateFormatError] = useState(false);
  const handleDateRangeDraft = useCallback((draft: DateRangeDraft) => {
    dateRangeDraftRef.current = draft;
    if (
      dateRangeErrorForApply(
        draft,
        draft.captureTimeFrom,
        draft.captureTimeTo,
      ) === null
    )
      setShowDateFormatError(false);
  }, []);

  // The applied state, readable from the seed effect below without making
  // that effect depend on it (which would re-seed, and clobber, the draft
  // mid-edit whenever the page re-applies — e.g. a chip cleared from the
  // header while the modal is open).
  const appliedFilterStateRef = useRef(filterState);
  const appliedDeploymentsRef = useRef(selectedDeployments);
  useEffect(() => {
    appliedFilterStateRef.current = filterState;
  }, [filterState]);
  useEffect(() => {
    appliedDeploymentsRef.current = selectedDeployments;
  }, [selectedDeployments]);

  /**
   * Re-seed the draft from the applied state each time the modal opens. The
   * component stays mounted while hidden, so this is what discards a
   * cancelled edit. Runs once per open (depends on `isOpen` alone) and reads
   * the applied values through refs so a re-apply while open leaves the
   * reviewer's in-progress edits untouched.
   */
  useEffect(() => {
    if (!isOpen) return;
    draftDirtyRef.current = false;
    setDraft(appliedFilterStateRef.current);
    setDraftDeployments(appliedDeploymentsRef.current);
    setShowDateFormatError(false);
    setOpenSeq((n) => n + 1);
  }, [isOpen, setDraft, setDraftDeployments]);

  /**
   * While the modal is open and the draft is still pristine, follow the
   * applied state. The cache-restore and URL-param effects resolve the
   * project's scope asynchronously and can finish after the modal is already
   * open; without this, the open would have captured the pre-restore (empty)
   * values and Apply would commit them over the restored scope. Stops the
   * moment the reviewer edits a field, so a re-apply from the header while
   * the modal is open never clobbers an in-progress edit.
   */
  useEffect(() => {
    if (!isOpen || draftDirtyRef.current) return;
    setDraft(filterState);
    setDraftDeployments(selectedDeployments);
  }, [isOpen, filterState, selectedDeployments, setDraft, setDraftDeployments]);

  const confidenceError = validateFilters(draft).errors.confidence;

  /**
   * Restore cached filters from localStorage on component mount.
   * This only runs once when there are no URL parameters.
   */
  useEffect(() => {
    const restoreCachedFilters = async () => {
      // Don't restore if we have URL parameters, already have a selected project, or already restored
      if (
        projectGuid ||
        deploymentGuid ||
        imageName ||
        selectedProject ||
        hasRestoredFilters.current
      ) {
        hasRestoredFilters.current = true;
        return;
      }
      if (projects.length === 0) {
        // Wait for projects to load
        if (getCachedFilterValue(STORAGE_KEYS.PROJECT_ID)) {
          setLoadingPage(true);
        }
        return;
      }
      // Mark as restored to prevent running again
      hasRestoredFilters.current = true;
      const projectId = getCachedFilterValue(STORAGE_KEYS.PROJECT_ID);
      if (projectId) {
        try {
          const project = projects.find(
            (p: ProjectResponseDto) => p.project_id === projectId,
          );
          if (!project) {
            console.error(
              "Cached project not found in available projects for user.",
            );
            setLoadingPage(false);
            return;
          }
          const result = await handleSelectProject(project);
          if (!result) {
            console.error("Failed to fetch deployments for cached project.");
            setLoadingPage(false);
            return;
          }
          // Location/Sensor are plain filter-state fields now — restore
          // whatever ids were cached directly, discarding nothing (unlike
          // Deployment below, they don't need to resolve against the fetched
          // list since they're sent to the backend as plain id filters).
          const locationIds =
            getCachedFilterArray(STORAGE_KEYS.LOCATION_ID) ?? [];
          const sensorIds = getCachedFilterArray(STORAGE_KEYS.SENSOR_ID) ?? [];
          updateFilterState({ locationIds, sensorIds });

          const deploymentIds =
            getCachedFilterArray(STORAGE_KEYS.DEPLOYMENT_ID) ?? [];
          const picked =
            result.deployments?.filter((d) =>
              deploymentIds.includes(d.deployment_id),
            ) ?? [];
          setSelectedDeployments(picked);
          // Pass the just-restored ids explicitly — filterState.locationIds/
          // sensorIds won't reflect the updateFilterState call above until the
          // next render, and handleApplyFilters's defaults would otherwise
          // read the stale (still-empty) values and immediately erase them
          // from localStorage.
          await handleApplyFilters(
            project,
            picked,
            result.deployments ?? [],
            locationIds,
            sensorIds,
          );
        } catch (error) {
          console.error("Error restoring cached filters:", error);
          // Clear invalid cached data
          clearCachedFilters();
        } finally {
          setLoadingPage(false);
        }
      }
    };

    restoreCachedFilters();
    // Intentionally not including handleSelectProject in deps as we only want this to run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, deploymentGuid, projectGuid, imageName, selectedProject]);

  /**
   * Load data from URL params on component mount or when params change.
   */
  useEffect(() => {
    const loadDataFromUrlParams = async () => {
      if (projectGuid && selectedProject?.guid !== projectGuid) {
        setLoadingPage(true);
        if (projects.length > 0 && !selectedProject) {
          const project = projects.find((p) => p.guid === projectGuid);
          if (project) {
            const results = await handleSelectProject(project);
            const deps = results?.deployments;
            if (!deps) {
              console.error("Failed to fetch deployments for project.");
              return;
            }
            if (!deploymentGuid) {
              await handleApplyFilters(project, [], deps);
              return;
            }
            // If deploymentGuid is present, find the deployment and apply filters
            if (deps?.length > 0) {
              const deployment = deps.find((d) => d.guid === deploymentGuid);
              if (deployment) {
                setSelectedDeployments([deployment]);
                if (imageName) {
                  await applyFilters(project, [deployment], false);
                } else {
                  await handleApplyFilters(project, [deployment], deps);
                }
              }
            }
          }
        }
        if (loadingProjects === false && loadingDeployments === false) {
          setLoadingPage(false);
        }
      }
    };
    loadDataFromUrlParams();
  }, [
    projects,
    projectGuid,
    deploymentGuid,
    imageName,
    selectedProject,
    loadingProjects,
    loadingDeployments,
  ]);

  /**
   * Handles selecting a project by fetching the SAS URL for the project.
   * @param project the selected project
   */
  const handleSelectProject = async (project: ProjectResponseDto | null) => {
    if (project === selectedProject) {
      // Return cached values if the same project is selected
      return {
        deployments: deployments,
        locations: locations,
        sensors: sensors,
      };
    }
    // Reset other entities when project changes
    setDeployments([]);
    setLocations([]);
    setSensors([]);
    setSelectedDeployments([]);
    setDraftDeployments([]);
    setSelectedProject(project);
    if (!project) {
      navigate(`/review`, { replace: true });
      return;
    }
    setLoadingDeployments(true);
    setLoadingLocations(true);
    setLoadingSensors(true);
    // fetch deployments first since apply filter is disabled until deployments are loaded
    const deps = await fetchDeployments(project);
    const [locs, sens] = await Promise.all([
      fetchLocations(project),
      fetchSensors(project),
    ]);
    return {
      deployments: deps,
      locations: locs,
      sensors: sens,
    };
  };

  /**
   * Fetch every deployment in the selected project. Location/Sensor/Deployment
   * are independent filters now (no narrowing one by another), so this always
   * fetches the full project list regardless of what's selected elsewhere.
   * @param project the selected project
   * @returns the deployments for the project
   */
  const fetchDeployments = async (
    project: ProjectResponseDto | null,
  ): Promise<DeploymentResponseDto[] | undefined> => {
    if (!project) {
      return;
    }
    setLoadingDeployments(true);
    let localDeps: DeploymentResponseDto[] = [];
    try {
      const tmp = await getDeployments({
        project_id: project.project_id,
        all_deployments: true,
      });
      localDeps = tmp.deployments;
      setDeployments(localDeps);
    } catch (error) {
      console.error("Error fetching deployments:", error);
      setDeployments([]);
    } finally {
      setLoadingDeployments(false);
    }
    return localDeps;
  };

  /**
   * Fetch locations for the selected project.
   * @param project the selected project
   * @returns the locations for the project
   */
  const fetchLocations = async (
    project: ProjectResponseDto | null,
  ): Promise<LocationResponseDto[] | undefined> => {
    if (!project) {
      return;
    }
    setLoadingLocations(true);
    const locs = await getLocations({
      project_id: project.project_id,
      all_locations: true,
    });
    setLocations(locs.locations);
    setLoadingLocations(false);
    return locs.locations;
  };

  /**
   * Fetch sensors for the selected project.
   * @param project the selected project
   * @returns the sensors for the project
   */
  const fetchSensors = async (
    project: ProjectResponseDto | null,
  ): Promise<SensorResponseDto[] | undefined> => {
    if (!project) {
      return;
    }
    setLoadingSensors(true);
    const sensors = await getSensors({
      project_id: project.project_id,
      all_sensors: true,
    });
    setSensors(sensors.sensors);
    setLoadingSensors(false);
    return sensors.sensors;
  };

  /**
   * Saves the selected filters to localStorage for persistence. Location and
   * Sensor ids are passed explicitly rather than read from the `filterState`
   * prop directly — `handleApplyFilters` defaults them to `filterState` for
   * its normal callers, but the cache-restore path calls this in the same
   * tick as `updateFilterState({ locationIds, sensorIds })`, before that
   * update has re-rendered `filterState`; reading the prop here would silently
   * re-persist the stale (still-empty) arrays and immediately erase the
   * restored cache. Deployment is passed explicitly for the same reason it
   * always was — it's the one dimension this panel manages itself.
   * @param project the selected project
   * @param pickedDeployments the explicitly selected deployments
   * @param locationIds the current Location filter ids
   * @param sensorIds the current Sensor filter ids
   */
  const saveFiltersToLocalStorage = useCallback(
    (
      project: ProjectResponseDto | null,
      pickedDeployments: DeploymentResponseDto[],
      locationIds: number[],
      sensorIds: number[],
    ) => {
      if (project) {
        setCachedFilterValue(STORAGE_KEYS.PROJECT_ID, project.project_id);
      } else {
        localStorage.removeItem(STORAGE_KEYS.PROJECT_ID);
      }
      setCachedFilterArray(STORAGE_KEYS.LOCATION_ID, locationIds);
      setCachedFilterArray(STORAGE_KEYS.SENSOR_ID, sensorIds);
      setCachedFilterArray(
        STORAGE_KEYS.DEPLOYMENT_ID,
        pickedDeployments.map((d) => d.deployment_id),
      );
    },
    [],
  );

  /**
   * Handles applying filters based on the explicitly selected deployments.
   * Location and Sensor are independent `ReviewFilterState` fields sent
   * straight through by `applyFilters`/`buildMediaQuery` — they no longer
   * factor into this scope decision at all. Resolving to exactly one
   * deployment keeps the deep-linkable single-deployment URL/behavior; any
   * other count (zero or several) is a project-scoped query.
   *
   * `locationIds`/`sensorIds` default to the current `filterState` and only
   * need to be passed explicitly by a caller that just changed them in the
   * same tick (via `updateFilterState`) and can't wait for the resulting
   * re-render — see `saveFiltersToLocalStorage`.
   * @param project the selected project
   * @param pickedDeployments the explicitly selected deployments (may be empty)
   * @param allDeployments the full deployment list for the project, used when none are explicitly picked
   * @param locationIds the current Location filter ids; defaults to `filterState.locationIds`
   * @param sensorIds the current Sensor filter ids; defaults to `filterState.sensorIds`
   * @returns void
   */
  const handleApplyFilters = useCallback(
    async (
      project: ProjectResponseDto | null,
      pickedDeployments: DeploymentResponseDto[],
      allDeployments: DeploymentResponseDto[] | null,
      locationIds: number[] = filterState.locationIds,
      sensorIds: number[] = filterState.sensorIds,
    ) => {
      // Save selected filters to localStorage
      saveFiltersToLocalStorage(
        project,
        pickedDeployments,
        locationIds,
        sensorIds,
      );
      if (!project) {
        await applyFilters(null, [], false);
        return;
      }

      if (pickedDeployments.length === 1) {
        // Exactly one deployment selected — filter to that deployment only.
        navigate(`/review/${project.guid}/${pickedDeployments[0].guid}`, {
          replace: true,
        });
        await applyFilters(project, pickedDeployments, false);
      } else if (pickedDeployments.length === 0) {
        // No deployment explicitly picked — whole project query.
        // isWholeProject=true tells the backend to query by project_id rather than deployment_ids.
        navigate(`/review/${project.guid}`, { replace: true });
        await applyFilters(project, allDeployments ?? [], true);
      } else {
        // Multiple deployments explicitly picked — query exactly those.
        navigate(`/review/${project.guid}`, { replace: true });
        await applyFilters(project, pickedDeployments, false);
      }
    },
    [
      applyFilters,
      navigate,
      saveFiltersToLocalStorage,
      filterState.locationIds,
      filterState.sensorIds,
    ],
  );

  /**
   * Exposed to the parent so the chip bar can dismiss the deployment scope
   * filter without reopening the panel. Clears the selection and re-applies
   * as a whole-project query.
   */
  useImperativeHandle(
    ref,
    () => ({
      clearScopeFilter: async () => {
        if (!selectedProject) return;
        setSelectedDeployments([]);
        await handleApplyFilters(selectedProject, [], deployments);
      },
    }),
    [selectedProject, deployments, handleApplyFilters, setSelectedDeployments],
  );

  const countSuffix = (n?: number) =>
    n === undefined ? "" : ` (${n.toLocaleString()})`;

  const reviewStatusOptions: {
    value: ReviewStatusFilter | null;
    label: string;
  }[] = [
    { value: null, label: t("All") },
    {
      value: "unreviewed",
      label:
        t("awaiting review") +
        countSuffix(counts?.by_review_status?.unreviewed),
    },
    {
      value: "reviewed",
      label: t("complete") + countSuffix(counts?.by_review_status?.reviewed),
    },
  ];

  const observationTypeOptions: {
    value: ObservationTypeFilter | null;
    label: string;
  }[] = [
    { value: null, label: t("All") },
    {
      value: "animal",
      label: t("animal") + countSuffix(counts?.by_observation_type?.animal),
    },
    {
      value: "human",
      label: t("human") + countSuffix(counts?.by_observation_type?.human),
    },
    {
      value: "vehicle",
      label: t("vehicle") + countSuffix(counts?.by_observation_type?.vehicle),
    },
    {
      value: "blank",
      label: t("blank") + countSuffix(counts?.by_observation_type?.blank),
    },
    {
      value: "unknown",
      label: t("unknown") + countSuffix(counts?.by_observation_type?.unknown),
    },
  ];

  const inferenceStateOptions: {
    value: InferenceStateFilter | null;
    label: string;
  }[] = [
    { value: null, label: t("All") },
    {
      value: "no_run",
      label: t("no run") + countSuffix(counts?.by_inference_state?.no_run),
    },
    {
      value: "in_progress",
      label:
        t("in progress") + countSuffix(counts?.by_inference_state?.in_progress),
    },
    {
      value: "completed",
      label:
        t("completed") + countSuffix(counts?.by_inference_state?.completed),
    },
    {
      value: "failed",
      label: t("failed") + countSuffix(counts?.by_inference_state?.failed),
    },
  ];

  const findOption = <T,>(options: { value: T; label: string }[], value: T) =>
    options.find((o) => o.value === value) ?? options[0];

  /**
   * Commits the draft to the page and runs the query — the one path through
   * which the modal's edits reach the grid, the chip row and the URL/cache.
   * An invalid draft (confidence min > max) is not committed: the inline error
   * is already showing and the modal stays open for the reviewer to fix it.
   * Nor is a draft whose date boxes show something it does not hold (a
   * typed start after the end, or an incomplete date): the date range's
   * message shows and the modal stays open (AKL-1007).
   * Nothing is committed while a newly picked project's deployments are still
   * loading either — the Apply button is disabled then, but Enter reaches
   * this callback from the body and from number/date fields, and applying
   * against the emptied `deployments` list would blank every card's
   * deployment/location/sensor. Reads the draft through its refs, not the
   * state, so a value set earlier in the same key event (react-datepicker's
   * Enter-picks-a-day fires onChange before the Enter reaches the document
   * listener) is what gets applied. Location/Sensor ids are passed
   * explicitly because `handleApplyFilters` otherwise defaults them from
   * the `filterState` prop, which has not re-rendered with the commit in
   * this same tick.
   */
  const handleApply = useCallback(async () => {
    if (loadingDeployments) return;
    const next = draftRef.current;
    const nextDeployments = draftDeploymentsRef.current;
    if (!validateFilters(next).valid) return;
    const dateDraft = dateRangeDraftRef.current;
    if (
      dateDraft &&
      dateRangeErrorForApply(
        dateDraft,
        next.captureTimeFrom,
        next.captureTimeTo,
      ) !== null
    ) {
      setShowDateFormatError(true);
      return;
    }
    updateFilterState(next);
    setSelectedDeployments(nextDeployments);
    await handleApplyFilters(
      selectedProject,
      nextDeployments,
      deployments,
      next.locationIds,
      next.sensorIds,
    );
  }, [
    loadingDeployments,
    deployments,
    selectedProject,
    updateFilterState,
    setSelectedDeployments,
    handleApplyFilters,
  ]);

  /**
   * Clears every field except the selected Project (which isn't a filter, and
   * clearing it would empty every other field's options), then commits and
   * re-applies immediately through the same path as Apply: whole-project
   * query, `/review/:projectGuid` URL, cache rewritten, chips emptied, modal
   * closed. Reset used to only stage the cleared values for a later Apply,
   * which read as applied because the chips emptied at once while the grid
   * stayed filtered (QA Swarm UX-001 on AKL-764).
   */
  const handleResetFilters = async () => {
    // Same gate as Apply: while a newly picked project's deployments are still
    // loading, `deployments` is the emptied list, and applying against it
    // would leave every card's deployment/location/sensor blank.
    if (loadingDeployments) return;
    const cleared = emptyFilterState();
    setDraft(cleared);
    setDraftDeployments([]);
    updateFilterState(cleared);
    setSelectedDeployments([]);
    await handleApplyFilters(selectedProject, [], deployments, [], []);
  };

  /**
   * Focuses the first enabled filter field when the dialog opens (AKL-764
   * AC4). It searches the body, not the whole dialog, because the header row's
   * Close button comes first in the DOM; it falls back to the dialog only if
   * no field is enabled. Deliberately depends on `isOpen` alone — this must
   * run once per open, not every time the keydown-listener effect below
   * re-subscribes (e.g. on every filter edit, since its handler closes over
   * `selectedProject`/`selectedDeployments`/`deployments`/`handleApplyFilters`),
   * or typing anywhere in the modal would keep yanking focus back to the first
   * field after every keystroke.
   */
  useEffect(() => {
    if (!isOpen) return;
    const firstEnabled = (root: HTMLElement | null) =>
      Array.from(
        root?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
      ).find((el) => !el.hasAttribute("disabled"));
    (firstEnabled(bodyRef.current) ?? firstEnabled(dialogRef.current))?.focus();
  }, [isOpen]);

  /**
   * While open: traps Tab within the dialog, closes on Escape, and submits
   * Apply on Enter.
   *
   * Escape follows the "close the inner dropdown first" rule by looking at
   * what is rendered inside the dialog: an open react-select menu (its
   * MenuList carries `role="listbox"`) or an open react-datepicker calendar
   * (its day grid carries `role="listbox"` too — verified in react-select
   * 5.10.2 and react-datepicker 7.6.0, and pinned by the calendar test). If
   * one is present the child owns this Escape and closes itself; otherwise
   * the modal closes, whatever has focus. It cannot use `defaultPrevented`
   * for this: react-select 5.x calls preventDefault() on Escape even with
   * its menu closed, so a keyboard user parked in any of the eight dropdown
   * inputs could never close the modal (QA Swarm UX-003 on AKL-764). The
   * DOM check works because both `SelectInput` (with `usePortal` off, as
   * here) and `SingleDatePicker` render their popups inside the dialog — a
   * portaled menu here would break it.
   *
   * The Escape check runs in the CAPTURE phase on `document`, i.e. before
   * the event reaches the input and before react-select / react-datepicker
   * have seen it, so the DOM still shows whether a popup was open when the
   * key went down. It used to run in the bubble phase, after the child's
   * handler, on the assumption that React would not have flushed the child's
   * "menu closed" state yet. That holds in jsdom — testing-library's
   * `fireEvent` dispatches from JS, and a JS-initiated dispatch runs every
   * listener before any microtask — but not in a browser: for a real key
   * press the browser runs a microtask checkpoint between listeners, React
   * 18 (`createRoot`) flushes the child's SyncLane update in that microtask
   * right after the root container's listener, and by the time `document`'s
   * bubble listener ran the listbox was already gone, so one Escape closed
   * the dropdown AND the modal (found in Chrome on dev; the existing jsdom
   * test could not see it).
   *
   * Tab and Enter stay in the bubble phase because they bail out when the
   * key event was already handled (`defaultPrevented`) by a nested
   * react-select — e.g. its Enter-picks-an-option handling — so those take
   * precedence over the modal's own shortcuts, and that flag is only set
   * once the child's handler has run. Enter inside a date picker's own text
   * field is the one exception: react-datepicker calls preventDefault() for
   * its own internal Enter handling (closing the calendar / confirming the
   * keyboard-highlighted day) even when the modal still wants Enter to apply
   * filters there, matching every other field.
   */
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const getFocusable = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled"));

    const hasOpenInnerPopup = () =>
      dialog.querySelector('[role="listbox"]') !== null;

    // Capture phase: runs before the child's own Escape handler and before any
    // React flush, so a popup in the DOM here was open when the key was pressed.
    const handleEscapeCapture = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // A dialog layered above this one owns the keyboard (AKL-798); same guard as below.
      if (isTopLayerOpen()) return;
      if (hasOpenInnerPopup()) return;
      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // A dialog layered above this one owns the keyboard (AKL-798). Without this, typing in
      // the Report a problem dialog over an open filter panel reaches here: the Enter branch
      // below bails on buttons and on most inputs, but not on a TEXTAREA, so pressing Enter
      // in "What happened?" applied the filters underneath. Same guard as the one in
      // FullScreenImageReview, ManageReviewPage, PointToSpeciesModal and ObservationEditor.
      if (isTopLayerOpen()) return;
      // Escape is handled by the capture-phase listener above.
      if (e.key === "Escape") return;
      const active = document.activeElement;
      const isDateInput =
        active instanceof HTMLInputElement &&
        active.closest(".react-datepicker-wrapper") !== null;
      if (e.defaultPrevented && !(isDateInput && e.key === "Enter")) return;
      if (e.key === "Tab") {
        const focusable = getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (e.key === "Enter") {
        // Don't hijack Enter from a focused button (avoid double-firing it)
        // or most text inputs (react-select's search boxes rely on Enter to
        // pick the highlighted option) — except date inputs, where Enter
        // applying filters matches every other field. A fully-typed date
        // is already committed to filterState via its own onChange by the
        // time Enter fires here, so this just applies whatever's currently
        // selected, same as the number inputs below.
        if (active instanceof HTMLButtonElement) return;
        if (
          active instanceof HTMLInputElement &&
          active.type !== "number" &&
          !isDateInput
        )
          return;
        e.preventDefault();
        handleApply();
      }
    };
    document.addEventListener("keydown", handleEscapeCapture, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleEscapeCapture, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, handleApply]);

  return (
    <div className="flex flex-row w-full">
      <div className="text-left flex flex-col md:flex-row justify-between items-end ">
        <div
          className={`${tailwindClassnames.modalOverlay} z-50`}
          onClick={() => onClose()}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            className={`${tailwindClassnames.modalPanel} max-w-2xl`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* No dialog title today — this panel historically had none, and
                adding one is a copy decision pending sign-off. The header row
                hosts only the close button (ml-auto pins it right). */}
            <div className={tailwindClassnames.modalHeaderRow}>
              <button
                type="button"
                className={`ml-auto ${tailwindClassnames.modalCloseButton}`}
                onClick={() => onClose()}
                aria-label={t("Close")}
              >
                &times;
              </button>
            </div>
            <div
              ref={bodyRef}
              className={`${tailwindClassnames.modalBody} space-y-4`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <SelectInput
                  options={projects.map((project) => ({
                    value: project,
                    label: project.project_name,
                  }))}
                  className="w-full bg-storm-blue rounded h-full shadow text-left text-sm"
                  isDisabled={loadingProjects}
                  isLoading={loadingProjects}
                  isClearable={true}
                  isSearchable={true}
                  value={
                    selectedProject === null
                      ? null
                      : {
                          value: selectedProject,
                          label: selectedProject?.project_name,
                        }
                  }
                  isMulti={false}
                  name={t("Project")}
                  onChange={(e: any) => {
                    // Remove deployment and project from URL on project change from dropdown
                    navigate(`/review`, { replace: true });
                    handleSelectProject(e ? e.value : null);
                  }}
                  errors={""}
                  searchBar={false}
                />
                <SelectInput
                  options={deployments.map((dep: DeploymentResponseDto) => ({
                    value: dep,
                    label: dep.deployment_name,
                  }))}
                  className="w-full bg-storm-blue rounded h-full shadow text-left text-sm"
                  isDisabled={false}
                  isLoading={loadingDeployments}
                  isClearable={true}
                  isSearchable={true}
                  value={draftDeployments.map((dep) => ({
                    value: dep,
                    label: dep.deployment_name,
                  }))}
                  isMulti={true}
                  name={t("Deployment")}
                  onChange={(selected: any) =>
                    editDraftDeployments(
                      (selected ?? []).map((o: any) => o.value),
                    )
                  }
                  errors={""}
                  searchBar={false}
                />
              </div>

              <div className="border-t border-off-white-dark dark:border-storm-blue pt-4 mt-2">
                <h3 className={tailwindClassnames.dropdownLabel}>
                  {t("Filters")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <TaxonMultiSelect
                    value={draft.predictedTaxonIds}
                    onChange={(ids) => updateDraft({ predictedTaxonIds: ids })}
                  />
                  <SelectInput
                    options={locations.map((loc) => ({
                      value: loc,
                      label: loc.location_name,
                    }))}
                    className="w-full bg-storm-blue rounded shadow text-left text-sm"
                    isDisabled={false}
                    isLoading={loadingLocations}
                    isClearable={true}
                    isSearchable={true}
                    value={locations
                      .filter((loc) =>
                        draft.locationIds.includes(loc.location_id),
                      )
                      .map((loc) => ({ value: loc, label: loc.location_name }))}
                    isMulti={true}
                    name={t("Location")}
                    onChange={(selected: any) =>
                      updateDraft({
                        locationIds: (selected ?? []).map(
                          (o: any) => o.value.location_id,
                        ),
                      })
                    }
                    errors={""}
                    searchBar={false}
                  />
                  <SelectInput
                    options={sensors.map((sensor) => ({
                      value: sensor,
                      label: sensor.sensor_name,
                    }))}
                    className="w-full bg-storm-blue rounded shadow text-left text-sm"
                    isDisabled={false}
                    isLoading={loadingSensors}
                    isClearable={true}
                    isSearchable={true}
                    value={sensors
                      .filter((sensor) =>
                        draft.sensorIds.includes(sensor.sensor_id),
                      )
                      .map((sensor) => ({
                        value: sensor,
                        label: sensor.sensor_name,
                      }))}
                    isMulti={true}
                    name={t("Sensor")}
                    onChange={(selected: any) =>
                      updateDraft({
                        sensorIds: (selected ?? []).map(
                          (o: any) => o.value.sensor_id,
                        ),
                      })
                    }
                    errors={""}
                    searchBar={false}
                  />
                  <SelectInput
                    options={reviewStatusOptions}
                    className="w-full bg-storm-blue rounded shadow text-left text-sm"
                    isDisabled={false}
                    isLoading={false}
                    isClearable={false}
                    isSearchable={false}
                    isMulti={false}
                    name={t("Review Status")}
                    value={findOption(reviewStatusOptions, draft.reviewStatus)}
                    onChange={(e: any) =>
                      updateDraft({ reviewStatus: e ? e.value : null })
                    }
                    errors={""}
                    searchBar={false}
                  />
                  <SelectInput
                    options={observationTypeOptions}
                    className="w-full bg-storm-blue rounded shadow text-left text-sm"
                    isDisabled={false}
                    isLoading={false}
                    isClearable={false}
                    isSearchable={false}
                    isMulti={false}
                    name={t("Observation Type")}
                    value={findOption(
                      observationTypeOptions,
                      draft.observationType,
                    )}
                    onChange={(e: any) =>
                      updateDraft({ observationType: e ? e.value : null })
                    }
                    errors={""}
                    searchBar={false}
                  />
                  <SelectInput
                    options={inferenceStateOptions}
                    className="w-full bg-storm-blue rounded shadow text-left text-sm"
                    isDisabled={false}
                    isLoading={false}
                    isClearable={false}
                    isSearchable={false}
                    isMulti={false}
                    name={t("Inference State")}
                    value={findOption(
                      inferenceStateOptions,
                      draft.inferenceState,
                    )}
                    onChange={(e: any) =>
                      updateDraft({ inferenceState: e ? e.value : null })
                    }
                    errors={""}
                    searchBar={false}
                  />
                  <div className="flex flex-col">
                    <ReviewDateRangeInput
                      key={openSeq}
                      label={t("Date Range")}
                      captureTimeFrom={draft.captureTimeFrom}
                      captureTimeTo={draft.captureTimeTo}
                      onChange={(captureTimeFrom, captureTimeTo) =>
                        updateDraft({ captureTimeFrom, captureTimeTo })
                      }
                      onDraftChange={handleDateRangeDraft}
                      showFormatError={showDateFormatError}
                    />
                    <span className="text-xs text-off-black-light dark:text-greyed-text mt-1">
                      {t(
                        "Setting a date range excludes photos with no capture time.",
                      )}
                    </span>
                  </div>
                  <ConfidenceRangeInput
                    min={draft.confidenceMin}
                    max={draft.confidenceMax}
                    onChange={(min, max) => {
                      // Full range (0–1) means "no confidence filter" — store null
                      // so the dimension isn't flagged active and the query
                      // identity/cache doesn't churn for a no-op restriction.
                      const isFullRange = min <= 0 && max >= 1;
                      updateDraft({
                        confidenceMin: isFullRange ? null : min,
                        confidenceMax: isFullRange ? null : max,
                      });
                    }}
                    error={confidenceError}
                  />
                </div>
              </div>
            </div>
            <div className={tailwindClassnames.modalFooter}>
              <div className="flex w-full items-center justify-between">
                <button
                  type="button"
                  className={tailwindClassnames.btnGhost}
                  onClick={handleResetFilters}
                  disabled={selectedProject === null || loadingDeployments}
                >
                  {t("Reset filters")}
                </button>
                <div className="flex gap-2.5">
                  <button
                    type="button"
                    className={tailwindClassnames.btnSecondary}
                    onClick={() => onClose()}
                  >
                    {selectedProject === null ? t("Close") : t("Cancel")}
                  </button>
                  <button
                    type="button"
                    className={tailwindClassnames.btnPrimary}
                    onClick={() => {
                      handleApply();
                    }}
                    disabled={selectedProject === null || loadingDeployments}
                  >
                    {t("Apply")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ReviewPageFilter;
